<template>
  <v-dialog v-model="dialog" max-width="500px">
    <template #activator="{on}">
      <slot name="activator" :on="on" />
    </template>

    <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }">
      <form
        novalidate
        autocomplete="off"
        @submit.prevent="handleSubmit(onSubmit)"
      >
        <v-card>
          <v-card-title
            class="primary white--text fs-16 lh-20 font-weight-bold py-4 px-6"
          >
            <span class="pa-0">{{ request.hcf_code }}</span>
            <v-spacer></v-spacer>
            <v-btn color="white" fab x-small depressed>
              <v-icon
                @click="dialog = false"
                color="primary"
                class="fs-18 font-weight-bold"
              >
                close
              </v-icon>
            </v-btn>
          </v-card-title>

          <div v-if="loading" class="text-center py-5">
            <v-progress-circular color="primary" indeterminate />
          </div>

          <v-card-text v-else class="py-2">
            <mou-table v-model="mouFiles" :form-request="formRequest" />

            <v-row
              v-if="canUpload"
              class="fill-height"
              align="center"
              justify="center"
            >
              <v-col cols="12" class="text-center">
                <div class="mb-2 d-block fs-12 font-weight-medium text-left">
                  Final MoU & Additional Document
                </div>
                <v-row v-for="(file, index) in files" :key="index" dense>
                  <v-col>
                    <ValidationProvider
                      slim
                      name="File"
                      rules="required"
                      v-slot="{ errors }"
                      :vid="`fileInput${index}`"
                    >
                      <v-file-input
                        :id="`fileInput${index}`"
                        v-model="file.model"
                        prepend-icon=""
                        append-icon="mdi-attachment"
                        placeholder="File"
                        :error-messages="errors"
                        outlined
                      />
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="auto" class="pl-4">
                    <v-btn
                      v-if="index === 0"
                      color="success"
                      fab
                      small
                      depressed
                      @click="addFile"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      color="error"
                      fab
                      small
                      depressed
                      @click="removeFile(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
          <template v-if="canUpload || canExecute">
            <v-divider />
            <v-card-actions class="px-4">
              <v-row>
                <v-col cols="12" sm="6" class="text-center">
                  <v-btn
                    v-if="canUpload"
                    type="submit"
                    class="btn-medium white--text mr-4"
                    color="menuActive"
                    depressed
                    :loading="submitting"
                    block
                  >
                    Upload
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="6" class="text-center">
                  <v-btn
                    type="button"
                    class="btn-medium white--text"
                    color="menuActive"
                    depressed
                    @click="executeForm"
                    :disabled="submitting || !canExecute"
                    :loading="executing"
                    block
                  >
                    Complete
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </template>
        </v-card>
      </form>
    </ValidationObserver>
  </v-dialog>
</template>

<script>
import { formatDate } from '@/mixins/filter';
import { mapActions, mapState } from 'vuex';
import Swal from 'sweetalert2';
import {
  mapEvents,
  PAYLOAD_DATE_FORMAT,
  reformatDate
} from '@/utils/FormRequestHelper';
import MouTable from '@/components/SSHCF/MouTable';

export default {
  components: { MouTable },
  props: {
    id: {
      required: true
    }
  },
  data: () => ({
    files: [
      {
        model: null
      }
    ],
    mouFiles: [],
    dialog: false,
    loading: false,
    submitting: false,
    executing: false
  }),
  computed: {
    ...mapState('sshcf', ['formRequest', 'regions']),
    request() {
      return this.formRequest || {};
    },
    status() {
      return this.formRequest?.local_status;
    },
    canUpload() {
      return ['Fully Approved', 'Completing Doc'].includes(this.status);
    },
    canExecute() {
      return this.status === 'Completing Doc';
    }
  },
  watch: {
    dialog: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.fetch();
        }
      }
    }
  },
  methods: {
    ...mapActions('sshcf', ['getFormRequestDetail']),
    async fetch() {
      this.loading = true;
      await this.fetchRegions();
      await this.fetchDetail();
      this.loading = false;
    },
    fetchDetail() {
      return this.getFormRequestDetail(this.id);
    },
    fetchRegions() {
      return this.$store.dispatch('sshcf/getRegions', {
        paginate: false
      });
    },
    addFile() {
      this.files.push({
        model: null
      });
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    async executeForm() {
      this.executing = true;
      const res = await this.$store.dispatch('sshcf/executeFormRequest', {
        formId: this.id,
        payload: {
          execute: true
        }
      });
      this.executing = false;

      if (res.code === 200) {
        Swal.fire('Success!', res.message.text_en, 'success');
        this.$store.dispatch('sshcf/syncData', 'form_request');
      } else {
        Swal.fire('Failed!', res.message.text_en, 'error');
      }
    },
    async onSubmit() {
      this.submitting = true;

      const uploaded = await Promise.all(
        this.files.map(file => {
          const data = new FormData();
          data.append('file', file.model);
          return this.$store.dispatch('sshcf/uploadFile', data);
        })
      );
      const mouFiles = uploaded.map((res, index) => ({
        url: res.data,
        name: this.files[index].model.name
      }));
      const events = mapEvents(this.request);

      const region = this.regions.find(
        item => item.r_name === this.request.region
      );
      const payments = this.request.payment.map(item => ({
        percent: item.percent,
        amount: item.int
      }));

      const data = {
        requestor: {
          region: this.request.region,
          district: this.request.district,
          district_manager: this.request.district_manager
        },

        general_information: {
          created_date: formatDate(
            this.request.created_date,
            PAYLOAD_DATE_FORMAT
          ),
          preval_number: this.request.preval_number,
          po_number: 'Non PO Activity',
          gl_account: '811112',
          cost_center: '400455',
          io_number: this.request.io_number,
          amount_of_support: this.request.support_amount
        },

        first_party_information: {
          company_name: this.request.company_name,
          company_address: this.request.company_address,
          name_of_signatory: this.request.first_party_name_of_signatory,
          title_of_signatory: this.request.first_party_title_of_signatory
        },

        second_party_information: {
          hospital_name: this.request.hospital,
          hospital_description: this.request.hospital_description,
          hospital_address: this.request.hospital_address,
          hospital_city: this.request.hospital_city,
          hospital_telephone: this.request.second_party_telephone,
          hospital_fax: this.request.second_party_fax,
          hospital_email: this.request.second_party_email,
          vendor_code: this.request.vendor_code,
          vendor_name: this.request.vendor_name,
          bank_name: this.request.bank_name,
          bank_branch: this.request.bank_branch,
          bank_account_number: this.request.account_number,
          signatory_name: this.request.second_party_name_of_signatory,
          signatory_title: this.request.second_party_title_of_signatory,
          remarks: this.request.remarks
        },

        agreement_information: {
          date_start: formatDate(this.request.start_date, PAYLOAD_DATE_FORMAT),
          date_end: reformatDate(this.request.end_date, PAYLOAD_DATE_FORMAT),
          amount_of_support: this.request.support_amount,
          // payment_type: this.request.payment.length > 0 ? 2 : 1,
          payments
        },

        event_information: {
          // total_amount: 0,
          events
        },

        supporting_documents: {
          invoice: {
            name: this.request.document.invoice_name,
            url: this.request.document.invoice_url
          },
          proposal: {
            name: this.request.document.proposal_name,
            url: this.request.document.proposal_url
          },
          mou: {
            name: this.request.document.mou_name,
            url: this.request.document.mou_url
          },
          summary_of_prevalidation: {
            name: this.request.document.summary_of_prevalidation_name,
            url: this.request.document.summary_of_prevalidation_url
          },
          final_mou: [...this.mouFiles, ...mouFiles]
        },

        signature_information_of_first_party: {
          region_address: region.r_office_address,
          phone_number: region.r_office_phone_number,
          fax_number: region.r_office_fax_number,
          email: region.r_manager_email,
          pic_name: region.r_manager_name
        }
      };

      const res = await this.$store.dispatch('sshcf/uploadFinalMOU', {
        formId: this.request.form_id,
        payload: data
      });
      this.submitting = false;

      if (res.code === 200) {
        Swal.fire('Success!', res.message.text_en, 'success');
        this.files = [
          {
            model: null
          }
        ];
        await this.$store.dispatch('sshcf/syncData', 'form_request');
        this.fetch();
      } else {
        Swal.fire('Failed!', res.message.text_en, 'error');
      }
    }
  }
};
</script>

<style scoped></style>
